import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sparkles, TrendingUp, MapPin, RefreshCw, Loader2, Trophy, Flame,
  Users, Info, Check, CalendarClock, ChevronRight, Shuffle, Compass,
  ChevronDown, CalendarDays, DoorOpen, Building2, History, Landmark,
  CalendarRange, ArrowRight,
} from "lucide-react";

const card = "rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]";

const HORIZONS = [
  { v: "4", label: "1 mois (4 sem.)" },
  { v: "8", label: "2 mois (8 sem.)" },
  { v: "12", label: "3 mois (12 sem.)" },
  { v: "26", label: "6 mois (26 sem.)" },
  { v: "52", label: "12 mois (52 sem.)" },
];

const HISTORIQUES = [
  { v: "0", label: "Historique complet" },
  { v: "12", label: "12 derniers mois" },
  { v: "24", label: "24 derniers mois" },
  { v: "36", label: "36 derniers mois" },
];

const TIER_LABEL = { top: "Ville forte", bon: "Bon potentiel", moyen: "À explorer" };

// -- Dates réelles et lisibles en français ----------------------------------
function frShortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  } catch { return String(iso).slice(0, 10); }
}
function frShortDateYear(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
}
function frFullDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
}
function frMonthYear(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  } catch { return ""; }
}
function agoLabel(days) {
  if (days == null) return "Jamais renseigné";
  if (days <= 1) return "Cette semaine";
  if (days < 31) return `Il y a ${days} j`;
  const months = Math.round(days / 30);
  if (months < 12) return `Il y a ${months} mois`;
  const years = Math.floor(days / 365);
  const rem = Math.round((days - years * 365) / 30);
  return rem > 0 ? `Il y a ${years} an${years > 1 ? "s" : ""} ${rem} m` : `Il y a ${years} an${years > 1 ? "s" : ""}`;
}

// Couleur de cellule : verte si « ville forte » (excellent), sinon orange.
function cellTheme(cell) {
  if (cell.excellent) {
    return { border: "rgba(31,157,85,0.38)", bg: "rgba(31,157,85,0.08)", ink: "#166534", accent: "#1F9D55" };
  }
  return { border: "rgba(238,114,46,0.34)", bg: "rgba(238,114,46,0.07)", ink: "#8A3A10", accent: "#EE722E" };
}

function KpiCard({ icon: Icon, label, value, suffix, tint, testid }) {
  return (
    <div className={`${card} flex items-center gap-3 p-4`} data-testid={testid}>
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ background: tint.bg, color: tint.fg }}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">{label}</div>
        <div className="truncate text-xl font-extrabold text-ink" title={String(value)}>
          {value}{suffix ? <span className="ml-0.5 text-sm font-bold text-muted-ink">{suffix}</span> : null}
        </div>
      </div>
    </div>
  );
}

function RecencyChip({ days }) {
  const stale = days != null && days >= 180;
  const style = stale
    ? { background: "rgba(180,83,9,0.12)", color: "#B45309" }
    : { background: "rgba(31,157,85,0.12)", color: "#166534" };
  return (
    <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold" style={style}>
      <History className="h-2.5 w-2.5" />{stale ? "À relancer" : "Actif"}
    </span>
  );
}

// Carte de REPLI (ville de secours pour la semaine).
function AltCityCard({ alt, rank }) {
  const t = cellTheme(alt);
  return (
    <div
      data-testid={`pap-alt-${alt.city}`}
      className="flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left"
      style={{ borderColor: t.border, background: t.bg, borderLeftWidth: 3, borderLeftColor: t.accent }}>
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md text-[10px] font-extrabold"
        style={{ background: `${t.accent}22`, color: t.accent }} title={`Repli n°${rank}`}>{rank}</span>
      <span className="min-w-0 flex-1">
        <span className="block break-words text-[11px] font-semibold leading-tight text-ink" title={alt.city}>{alt.city}</span>
        <span className="block break-words text-[9px] text-muted-ink">
          {[alt.dept ? `Dép. ${alt.dept}` : null, agoLabel(alt.days_since)].filter(Boolean).join(" · ")}
        </span>
      </span>
      <span className="shrink-0 text-right text-[10px] font-extrabold" style={{ color: t.accent }}>≈{alt.avg_per_day}<span className="text-[8px] font-bold">/j</span></span>
    </div>
  );
}

// Cellule d'une semaine : ville proposée + popover détaillé (volume, âge, dernier
// passage, associations, meilleurs recruteurs, 3 replis).
function IdealCell({ cell, weekMeta }) {
  const navigate = useNavigate();
  if (!cell || cell.state === "none") {
    return (
      <div className="flex min-h-[96px] items-center justify-center rounded-xl border border-dashed border-[var(--border-soft)]"
        data-testid={`pap-cell-none-${weekMeta?.monday}`}>
        <span className="text-xs font-semibold text-muted-ink/50">Aucune ville</span>
      </div>
    );
  }
  const t = cellTheme(cell);
  const alts = cell.alternatives || [];
  const assocs = cell.associations || [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`pap-cell-${weekMeta?.monday}-${cell.city}`}
          className="flex min-h-[96px] w-full flex-col rounded-xl border p-2 text-left transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2"
          style={{ borderColor: t.border, background: t.bg, borderLeftWidth: 3, borderLeftColor: t.accent }}>
          <div className="flex items-center justify-between gap-1">
            <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold"
              style={{ background: `${t.accent}22`, color: t.ink }}>
              {cell.excellent ? <Flame className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />} ≈ {cell.avg_per_day} BS/j
            </span>
            {cell.tier && (
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide" style={{ color: t.accent }}>{TIER_LABEL[cell.tier]}</span>
            )}
          </div>
          <div className="mt-1 break-words text-[12px] font-bold leading-tight text-ink" title={cell.city}>{cell.city}</div>
          <div className="mt-0.5 flex items-start gap-1 text-[10px] text-muted-ink">
            <MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" />
            <span className="break-words">{cell.dept ? `Dép. ${cell.dept}` : cell.region_label}</span>
          </div>
          <div className="mt-auto flex items-center justify-between gap-1 pt-1.5">
            <span className="inline-flex items-center gap-1 rounded-md bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold text-ink ring-1 ring-inset ring-[var(--border-soft)]">
              <CalendarClock className="h-2.5 w-2.5" />{frShortDate(cell.suggested_date)}
            </span>
            <span className="inline-flex shrink-0 items-center gap-1">
              {alts.length > 0 && (
                <span className="rounded-full bg-white/80 px-1 text-[9px] font-bold text-muted-ink ring-1 ring-inset ring-[var(--border-soft)]" title={`${alts.length} ville(s) de repli`}>+{alts.length}</span>
              )}
              <ChevronRight className="h-3.5 w-3.5" style={{ color: t.accent }} />
            </span>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={12} className="w-[min(21rem,calc(100vw-1.5rem))] p-3" data-testid={`pap-pop-${cell.city}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="break-words text-sm font-extrabold leading-tight text-ink" title={cell.city}>{cell.city}</div>
            <div className="mt-0.5 flex items-start gap-1 text-[11px] text-muted-ink">
              <MapPin className="mt-0.5 h-3 w-3 shrink-0" />{cell.dept ? `Département ${cell.dept} · ` : ""}{cell.region_label}
            </div>
          </div>
          <span className="shrink-0 rounded-lg px-2 py-1 text-center" style={{ background: `${t.accent}18`, color: t.ink }}>
            <span className="block text-lg font-extrabold leading-none">{cell.avg_per_day}</span>
            <span className="block text-[8px] font-bold uppercase">BS / jour</span>
          </span>
        </div>

        {/* Semaine proposée — date réelle et complète */}
        <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-[var(--surface-2)] px-2 py-1.5 text-[11px] font-semibold capitalize text-ink">
          <CalendarClock className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          Semaine du {frFullDate(cell.suggested_date)}
        </div>

        {/* Chiffres clés — rendement historique de la ville */}
        <div className="my-2 grid grid-cols-3 gap-1.5 text-center">
          <div className="rounded-lg bg-[var(--surface-2)] px-1 py-1.5">
            <div className="text-sm font-extrabold text-ink">{cell.valid}</div>
            <div className="text-[9px] font-semibold uppercase text-muted-ink">BS valides</div>
          </div>
          <div className="rounded-lg bg-[var(--surface-2)] px-1 py-1.5">
            <div className="text-sm font-extrabold text-ink">{cell.days ?? "—"}</div>
            <div className="text-[9px] font-semibold uppercase text-muted-ink">jours passés</div>
          </div>
          <div className="rounded-lg bg-[var(--surface-2)] px-1 py-1.5">
            <div className="text-sm font-extrabold text-ink">{cell.avg_age ?? "—"}</div>
            <div className="text-[9px] font-semibold uppercase text-muted-ink">âge moyen</div>
          </div>
        </div>

        {/* Ancienneté du dernier passage */}
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-[var(--surface-2)] px-2 py-1.5">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-ink">
            <History className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Dernier passage
          </span>
          <span className="text-right text-[11px] font-extrabold capitalize text-ink" title={frFullDate(cell.last_date)}>
            {cell.last_date ? frShortDateYear(cell.last_date) : "—"}
            <span className="ml-1 font-semibold text-muted-ink">· {agoLabel(cell.days_since)}</span>
          </span>
        </div>

        {/* Associations : rendement (BS/j) propre à chacune sur la ville */}
        {assocs.length > 0 && (
          <div className="rounded-lg border border-[var(--border-soft)] p-2">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
              <Landmark className="h-3 w-3" /> Associations qui performent ici
            </div>
            <div className="space-y-1">
              {assocs.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <span className="inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: a.color }} title={a.name}>{a.acronym}</span>
                    <span className="truncate text-muted-ink">{a.bulletins} BS · {a.days} j</span>
                  </span>
                  <span className="shrink-0 font-extrabold text-ink">≈ {a.avg_per_day} <span className="text-[9px] font-bold text-muted-ink">/j</span></span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Meilleurs recruteurs : rendement (BS/j) + association */}
        {(cell.top_recruteurs || []).length > 0 && (
          <div className="mt-2 rounded-lg border border-[var(--border-soft)] p-2">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
              <Users className="h-3 w-3" /> Meilleurs recruteurs ici
            </div>
            {cell.top_recruteurs.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-2 py-0.5 text-[11px]">
                <span className="inline-flex min-w-0 flex-1 items-center gap-1.5">
                  {r.assoc && <span className="inline-flex shrink-0 items-center rounded px-1 py-0.5 text-[8px] font-bold text-white" style={{ background: r.assoc_color || "#64748B" }}>{r.assoc}</span>}
                  <span className="min-w-0 break-words font-semibold leading-snug text-ink">{r.name}</span>
                </span>
                <span className="shrink-0 text-right text-muted-ink">{r.bulletins} BS · <b className="text-ink">≈{r.avg_per_day}/j</b></span>
              </div>
            ))}
          </div>
        )}

        <Button size="sm" variant="outline" className="mt-2.5 h-8 w-full justify-center gap-1.5"
          data-testid={`pap-goto-planning-${cell.city}`} onClick={() => navigate("/planning-pap")}>
          <DoorOpen className="h-3.5 w-3.5" /> Ouvrir le planning porte-à-porte
        </Button>

        {/* Replis */}
        {alts.length > 0 && (
          <div className="mt-2.5 border-t border-[var(--border-soft)] pt-2" data-testid={`pap-alts-${cell.city}`}>
            <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
              <Shuffle className="h-3 w-3" /> Si cette ville ne donne rien — {alts.length} repli{alts.length > 1 ? "s" : ""}
            </div>
            <div className="space-y-1">
              {alts.map((a, i) => <AltCityCard key={`${a.city}-${i}`} alt={a} rank={i + 1} />)}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Cellule « mois » compacte : liste chaque semaine du mois pour la région.
function MonthCell({ items }) {
  const entries = (items || []).filter(({ cell }) => cell && cell.state === "proposed");
  if (entries.length === 0) {
    return (
      <div className="flex min-h-[64px] items-center justify-center rounded-xl border border-dashed border-[var(--border-soft)]">
        <span className="text-xs font-semibold text-muted-ink/40">—</span>
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="pap-monthcol">
      {entries.map(({ cell, weekMeta }, k) => {
        const green = cell.excellent;
        const accent = green ? "#1F9D55" : "#EE722E";
        const softBg = green ? "rgba(31,157,85,0.06)" : "rgba(238,114,46,0.05)";
        return (
          <div key={weekMeta?.monday || k}
            title={`${frShortDate(cell.suggested_date)} · ${cell.city} · ≈${cell.avg_per_day} BS/j`}
            data-testid={`pap-monthcell-${cell.city}-${k}`}
            className="flex w-full flex-col rounded-lg border p-1.5 text-left"
            style={{ borderColor: "var(--border-soft)", background: softBg, borderLeftWidth: 3, borderLeftColor: accent }}>
            <div className="flex items-center justify-between gap-1">
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold capitalize" style={{ color: accent }}>
                <CalendarClock className="h-3 w-3 shrink-0" /> {frShortDate(cell.suggested_date)}
              </span>
              <span className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-extrabold" style={{ color: accent }}>
                {green ? <Flame className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}≈{cell.avg_per_day}
              </span>
            </div>
            <div className="mt-0.5 break-words text-[11px] font-bold leading-snug text-ink">{cell.city}</div>
            <div className="mt-0.5 break-words text-[9px] text-muted-ink">{cell.dept ? `Dép. ${cell.dept}` : cell.region_label}</div>
          </div>
        );
      })}
    </div>
  );
}

// Carte MOBILE : une région = une carte pliable, semaines listées verticalement.
function MobileRegionCard({ row, weeksMeta, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const entries = row.cells
    .map((cell, ci) => ({ cell, weekMeta: weeksMeta[ci] }))
    .filter(({ cell }) => cell && cell.state === "proposed");
  if (entries.length === 0) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={`${card} overflow-hidden`} data-testid={`pap-mrow-${row.region_key}`}>
      <CollapsibleTrigger asChild>
        <button className="flex w-full items-start gap-2.5 p-3 text-left transition-colors hover:bg-[var(--surface-2)]"
          data-testid={`pap-mrow-toggle-${row.region_key}`}>
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl text-white" style={{ background: row.color }}>
            <Compass className="h-4.5 w-4.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="break-words text-[13px] font-extrabold leading-tight text-ink">{row.region_label}</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-ink">
              <span className="inline-flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{row.cities_count} villes</span>
              <span className="inline-flex items-center gap-1"><TrendingUp className="h-2.5 w-2.5" />{row.total_bulletins} bulletins</span>
            </div>
          </div>
          <span className="flex shrink-0 flex-col items-end gap-1">
            <span className="rounded-full bg-[rgba(238,114,46,0.12)] px-2 py-0.5 text-[10px] font-extrabold text-[var(--shariti-orange)]">
              {entries.length} semaine{entries.length > 1 ? "s" : ""}
            </span>
            <ChevronDown className={`h-4 w-4 text-muted-ink transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2.5 border-t border-[var(--border-soft)] bg-[var(--bg-subtle)] p-3">
          {entries.map(({ cell, weekMeta }) => (
            <div key={weekMeta.monday}>
              <div className="mb-1 flex items-center justify-between gap-2 text-[11px] font-semibold text-muted-ink">
                <span className="capitalize text-ink">{frShortDate(weekMeta.monday)}</span>
                <span>Semaine {weekMeta.week_number}</span>
              </div>
              <IdealCell cell={cell} weekMeta={weekMeta} />
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Chip de région (multi-sélection du périmètre).
function RegionChip({ region, active, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(region.key)}
      data-testid={`pap-region-chip-${region.key}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${
        active ? "border-transparent text-white" : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"
      }`}
      style={active ? { background: region.color } : undefined}>
      <span className="h-2 w-2 rounded-full" style={{ background: active ? "rgba(255,255,255,0.85)" : region.color }} />
      {region.label}
      {active && <Check className="h-3 w-3" />}
    </button>
  );
}

export default function PlanningIdealPap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [assoc, setAssoc] = useState("all");
  const [weeks, setWeeks] = useState("12");
  const [months, setMonths] = useState("0");
  const [minAvg, setMinAvg] = useState("");
  const [regions, setRegions] = useState(["NA_OCC", "PACA", "IDF"]);
  const [viewMode, setViewMode] = useState("weeks"); // "weeks" | "months"

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = { weeks: Number(weeks), months: Number(months), regions: regions.join(",") };
      if (assoc !== "all") params.association_id = assoc;
      if (minAvg !== "" && !Number.isNaN(Number(minAvg))) params.min_avg = Number(minAvg);
      const { data } = await api.get("/pap/ideal", { params });
      setData(data);
      if (minAvg === "" && data?.kpis?.min_avg != null) setMinAvg(String(data.kpis.min_avg));
    } catch (e) {
      setData(null); setError(true);
    } finally { setLoading(false); }
  }, [assoc, weeks, months, regions, minAvg]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assoc, weeks, months, regions]);

  const k = data?.kpis || {};
  const weeksMeta = data?.weeks || [];
  const rows = data?.rows || [];
  const isMonthly = viewMode === "months";
  const colW = 208;
  const monthColW = 178;
  const leftW = 264;

  const regionsAvailable = data?.regions_available || [
    { key: "NA_OCC", label: "Nouvelle-Aquitaine & Occitanie", color: "#1F9D55" },
    { key: "PACA", label: "Provence-Alpes-Côte d'Azur (13)", color: "#3B6CB7" },
    { key: "IDF", label: "Île-de-France (75 · 93 · 95)", color: "#EE722E" },
  ];

  const toggleRegion = (key) => {
    setRegions((prev) => {
      if (prev.includes(key)) {
        const next = prev.filter((r) => r !== key);
        return next.length ? next : prev; // garder au moins une région
      }
      return [...prev, key];
    });
  };

  const activeRegionLabels = useMemo(
    () => regionsAvailable.filter((r) => regions.includes(r.key)).map((r) => r.label),
    [regionsAvailable, regions],
  );

  // Repères de changement de mois pour l'en-tête de la grille.
  const monthSpans = useMemo(() => {
    const spans = [];
    weeksMeta.forEach((w, i) => {
      const label = frMonthYear(w.monday);
      const prev = spans[spans.length - 1];
      if (prev && prev.label === label) prev.count += 1;
      else spans.push({ label, count: 1, startIdx: i });
    });
    return spans;
  }, [weeksMeta]);

  return (
    <>
      <PageHeader
        title="Planning Idéal — Porte-à-porte"
        subtitle={`La tournée porte-à-porte que vous devriez viser sur ${(HORIZONS.find((h) => h.v === weeks)?.label || "3 mois").split(" (")[0]} — basée sur le rendement réel (bulletins par jour) des villes.`}
        action={
          <Button variant="outline" onClick={load} disabled={loading} data-testid="pap-ideal-refresh" className="gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Actualiser
          </Button>
        }
      />
      <PageBody>
        {/* Bandeau stratégie */}
        <div className="mb-4 overflow-hidden rounded-2xl border border-[rgba(238,114,46,0.25)] p-4"
          style={{ background: "linear-gradient(120deg, rgba(238,114,46,0.10), rgba(252,219,83,0.10))" }}
          data-testid="pap-ideal-strategy">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--shariti-orange)] text-white">
              <DoorOpen className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-extrabold text-ink">Passez d'un porte-à-porte subi à un porte-à-porte choisi.</div>
              <p className="mt-0.5 text-[12px] leading-snug text-muted-ink">
                Pour chaque région et chaque semaine, nous proposons la ville au <b>meilleur rendement</b> (bulletins générés
                par jour de présence), par <b>rotation</b> sur vos villes les plus productives. Les enseignes de magasins sont
                exclues : <b>seules les villes</b> sont proposées. Chaque semaine a <b>3 villes de repli</b>.
              </p>
            </div>
          </div>
        </div>

        {/* Périmètre géographique (multi-sélection = lignes de la grille) */}
        <div className="mb-3" data-testid="pap-ideal-regions">
          <label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-muted-ink">
            <Building2 className="h-3.5 w-3.5" /> Périmètre géographique (lignes du planning)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {regionsAvailable.map((r) => (
              <RegionChip key={r.key} region={r} active={regions.includes(r.key)} onToggle={toggleRegion} />
            ))}
          </div>
        </div>

        {/* Filtres */}
        <div className="mb-4 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-end">
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Association</label>
            <Select value={assoc} onValueChange={setAssoc}>
              <SelectTrigger className="h-9 w-full sm:w-[200px]" data-testid="pap-ideal-filter-assoc"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les associations</SelectItem>
                {(data?.associations || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.acronym} — {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Horizon (planning)</label>
            <Select value={weeks} onValueChange={setWeeks}>
              <SelectTrigger className="h-9 w-full sm:w-[170px]" data-testid="pap-ideal-filter-weeks"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HORIZONS.map((h) => <SelectItem key={h.v} value={h.v}>{h.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Historique analysé</label>
            <Select value={months} onValueChange={setMonths}>
              <SelectTrigger className="h-9 w-full sm:w-[180px]" data-testid="pap-ideal-filter-months"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HISTORIQUES.map((h) => <SelectItem key={h.v} value={h.v}>{h.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Seuil « excellent » (BS/j)</label>
            <div className="flex items-center gap-1.5">
              <Input type="number" step="0.5" min="0.5" value={minAvg} onChange={(e) => setMinAvg(e.target.value)}
                className="h-9 w-full sm:w-[100px]" data-testid="pap-ideal-filter-minavg" />
              <Button variant="outline" className="h-9 shrink-0" onClick={load} data-testid="pap-ideal-apply-minavg">OK</Button>
            </div>
          </div>
          <div className="w-full sm:ml-auto sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Affichage</label>
            <div className="inline-flex h-9 w-full items-center rounded-lg border border-[var(--border-soft)] bg-white p-0.5 sm:w-auto" data-testid="pap-ideal-viewmode">
              <button onClick={() => setViewMode("weeks")} data-testid="pap-ideal-view-weeks"
                className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-bold transition-colors sm:flex-none ${!isMonthly ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <CalendarClock className="h-3.5 w-3.5" /> Semaines
              </button>
              <button onClick={() => setViewMode("months")} data-testid="pap-ideal-view-months"
                title="Vue compacte mois par mois"
                className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-bold transition-colors sm:flex-none ${isMonthly ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <CalendarDays className="h-3.5 w-3.5" /> Mois
              </button>
            </div>
          </div>
        </div>

        {/* KPIs — orientés rendement (BS/jour), comme les sites privés */}
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[76px] rounded-2xl" />)
          ) : (
            <>
              <KpiCard icon={TrendingUp} label="BS/jour visés" value={k.total_expected_bs ?? 0} suffix="cumulé"
                tint={{ bg: "rgba(31,157,85,0.12)", fg: "#1F7A44" }} testid="pap-ideal-kpi-bs" />
              <KpiCard icon={Sparkles} label="Villes proposées" value={k.proposals ?? 0}
                tint={{ bg: "rgba(238,114,46,0.12)", fg: "#EE722E" }} testid="pap-ideal-kpi-proposals" />
              <KpiCard icon={Trophy} label="Meilleure ville" value={k.top_city || "—"} suffix={k.top_city_avg ? `· ${k.top_city_avg}/j` : ""}
                tint={{ bg: "rgba(59,108,183,0.12)", fg: "#3B6CB7" }} testid="pap-ideal-kpi-top" />
              <KpiCard icon={MapPin} label="BS/jour moyen / ville" value={k.avg_expected_bs ?? 0} suffix="BS/j"
                tint={{ bg: "rgba(124,58,237,0.12)", fg: "#7C3AED" }} testid="pap-ideal-kpi-avg" />
            </>
          )}
        </div>

        {/* Légende */}
        {!loading && data && (
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-muted-ink" data-testid="pap-ideal-legend">
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: "rgba(31,157,85,0.7)" }} /> Excellent (≥ {k.min_avg} BS/jour)</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: "rgba(238,114,46,0.7)" }} /> Bon potentiel</span>
            <span className="inline-flex items-center gap-1"><span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold" style={{ background: "rgba(180,83,9,0.12)", color: "#B45309" }}><History className="h-2.5 w-2.5" />À relancer</span> = plus de 6 mois sans passage</span>
          </div>
        )}

        {/* Rappel du périmètre actif */}
        {!loading && data && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-[rgba(59,108,183,0.25)] bg-[rgba(59,108,183,0.05)] px-3 py-2 text-[12px] leading-snug text-[#2A4A7A]" data-testid="pap-ideal-scope-banner">
            <Compass className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Périmètre analysé : <b>{activeRegionLabels.join(" · ")}</b>. {(k.excluded_stores ?? 0).toLocaleString("fr-FR")} bulletins « enseigne » ont été écartés (magasins) pour ne conserver que les villes.</span>
          </div>
        )}

        {/* Grille régions × semaines */}
        {loading ? (
          <Skeleton className="h-[360px] rounded-2xl" />
        ) : error ? (
          <div className={`${card} flex flex-col items-center justify-center px-6 py-16 text-center`} data-testid="pap-ideal-error">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[rgba(193,18,31,0.1)]"><Info className="h-6 w-6 text-[#C1121F]" /></div>
            <div className="mt-3 text-sm font-bold">Impossible de charger le planning idéal</div>
            <Button variant="outline" className="mt-4 gap-1.5" onClick={load} data-testid="pap-ideal-retry"><RefreshCw className="h-4 w-4" /> Réessayer</Button>
          </div>
        ) : rows.length === 0 ? (
          <div className={`${card} flex flex-col items-center justify-center px-6 py-16 text-center`} data-testid="pap-ideal-empty">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[var(--surface-2)]"><CalendarRange className="h-6 w-6 text-[var(--shariti-orange)]" /></div>
            <div className="mt-3 text-sm font-bold">Aucune proposition à afficher</div>
            <p className="mt-1 max-w-md text-xs text-muted-ink">Pas assez d'historique de bulletins sur ce périmètre. Élargissez le périmètre géographique ou la fenêtre historique.</p>
          </div>
        ) : (
          <>
            {/* MOBILE — cartes empilées par région */}
            <div className="space-y-3 lg:hidden" data-testid="pap-ideal-mobile">
              {rows.map((row, ri) => (
                <MobileRegionCard key={row.region_key} row={row} weeksMeta={weeksMeta} defaultOpen={ri === 0} />
              ))}
            </div>

            {/* DESKTOP — grille régions × semaines */}
            <div className={`${card} hidden overflow-hidden lg:block`} data-testid="pap-ideal-grid">
              <div className="overflow-auto max-h-[calc(100vh-240px)]">
                <div className="min-w-max">
                  <div className="sticky top-0 z-30 shadow-[0_2px_6px_-2px_rgba(0,0,0,0.12)]">
                    {isMonthly ? (
                      <div className="flex border-b border-[var(--border-soft)] bg-[var(--surface-2)]">
                        <div className="sticky left-0 z-30 shrink-0 bg-[var(--surface-2)] px-3 py-2.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_10px_-6px_rgba(0,0,0,0.18)]"
                          style={{ width: leftW }}>Région / Mois</div>
                        {monthSpans.map((m, i) => (
                          <div key={i} className="shrink-0 border-l border-[var(--border-soft)] px-3 py-2.5 text-center" style={{ width: monthColW }}>
                            <div className="text-[12px] font-extrabold capitalize text-ink">{m.label}</div>
                            <div className="text-[10px] text-muted-ink">{m.count} sem.</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div className="flex border-b border-[var(--border-soft)] bg-[var(--surface-2)]">
                          <div className="sticky left-0 z-30 shrink-0 bg-[var(--surface-2)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_10px_-6px_rgba(0,0,0,0.18)]"
                            style={{ width: leftW }}>Région / Semaine</div>
                          {monthSpans.map((m, i) => (
                            <div key={i} className="shrink-0 border-l border-[var(--border-soft)] px-3 py-1.5 text-[12px] font-extrabold capitalize text-ink"
                              style={{ width: m.count * colW }}>{m.label}</div>
                          ))}
                        </div>
                        <div className="flex border-b border-[var(--border-soft)] bg-white">
                          <div className="sticky left-0 z-30 shrink-0 bg-white px-3 py-2 shadow-[6px_0_10px_-6px_rgba(0,0,0,0.18)]" style={{ width: leftW }} />
                          {weeksMeta.map((w) => (
                            <div key={w.monday} className="shrink-0 border-l border-[var(--border-soft)] px-2 py-2 text-center" style={{ width: colW }}>
                              <div className="text-[12px] font-extrabold capitalize text-ink">{frShortDate(w.monday)}</div>
                              <div className="text-[10px] text-muted-ink">Semaine {w.week_number}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Lignes = régions */}
                  {rows.map((row, ri) => {
                    const rowBg = ri % 2 ? "#FAF9F7" : "#FFFFFF";
                    return (
                      <div key={row.region_key} className="flex items-stretch" style={{ background: rowBg }} data-testid={`pap-ideal-row-${row.region_key}`}>
                        <div className="sticky left-0 z-20 shrink-0 border-r border-[var(--border-soft)] px-3 py-2.5 shadow-[6px_0_10px_-6px_rgba(0,0,0,0.18)]"
                          style={{ width: leftW, background: rowBg }}>
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-white" style={{ background: row.color }}>
                              <Compass className="h-3.5 w-3.5" />
                            </span>
                            <span className="break-words text-[12px] font-extrabold leading-tight text-ink">{row.region_label}</span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-ink">
                            <span className="inline-flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{row.cities_count} villes</span>
                            <span className="inline-flex items-center gap-1"><TrendingUp className="h-2.5 w-2.5" />{row.total_bulletins} BS</span>
                          </div>
                        </div>
                        {isMonthly ? (
                          monthSpans.map((m, mi) => (
                            <div key={mi} className="shrink-0 border-l border-[var(--border-soft)] p-1.5" style={{ width: monthColW }}>
                              <MonthCell items={Array.from({ length: m.count }, (_, kk) => ({ cell: row.cells[m.startIdx + kk], weekMeta: weeksMeta[m.startIdx + kk] }))} />
                            </div>
                          ))
                        ) : (
                          row.cells.map((cell, ci) => (
                            <div key={ci} className="shrink-0 border-l border-[var(--border-soft)] p-1.5" style={{ width: colW }}>
                              <IdealCell cell={cell} weekMeta={weeksMeta[ci]} />
                            </div>
                          ))
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Vitrine top villes */}
        {!loading && data && (data.top_cities || []).length > 0 && (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-[var(--shariti-orange)]" />
              <h3 className="text-sm font-extrabold text-ink">Meilleures villes du périmètre</h3>
              <span className="text-[11px] text-muted-ink">— triées par rendement (BS/jour)</span>
            </div>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {(data.top_cities || []).map((s, i) => {
                const t = cellTheme(s);
                return (
                  <div key={`${s.region}-${s.city}`} data-testid={`pap-ideal-top-${s.city}`} className={`${card} flex items-start gap-3 p-3`}>
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl text-sm font-extrabold"
                      style={{ background: `${t.accent}1f`, color: t.accent }}>{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="break-words text-[12px] font-bold leading-tight text-ink" title={s.city}>{s.city}</div>
                      <div className="mt-0.5 flex items-start gap-1 text-[10px] text-muted-ink"><MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" /><span className="break-words">{s.region_label}</span></div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-extrabold" style={{ color: t.accent }}>≈{s.avg_per_day}</div>
                      <div className="text-[9px] font-semibold uppercase text-muted-ink">BS/j · {s.days}j</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
